import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { AgentPrompt } from '../types';
import PromptEditor from '../components/Admin/PromptEditor';
import './PromptManagerPage.css';

const PromptManagerPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<AgentPrompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPrompts();
      setPrompts(data);
      if (data.length > 0) {
        setSelectedPrompt((current) => current || data[0]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.detail || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!user?.is_admin) {
      navigate('/');
      return;
    }

    loadPrompts();
  }, [isAuthenticated, user, navigate, loadPrompts]);

  const handlePromptSelect = async (promptKey: string) => {
    try {
      const prompt = await apiClient.getPrompt(promptKey);
      setSelectedPrompt(prompt);
    } catch (err: any) {
      setError(err.detail || 'Failed to load prompt');
    }
  };

  const handlePromptSave = async (promptKey: string, updates: any) => {
    try {
      setSaving(true);
      await apiClient.updatePrompt(promptKey, updates);
      
      // Refresh the prompt list
      await loadPrompts();
      
      // Update the selected prompt
      if (selectedPrompt?.prompt_key === promptKey) {
        const updatedPrompt = await apiClient.getPrompt(promptKey);
        setSelectedPrompt(updatedPrompt);
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.detail || 'Failed to save prompt');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'core': '#3b82f6',
      'workflow': '#10b981',
      'system': '#f59e0b',
      'customer': '#8b5cf6',
      'default': '#6b7280'
    };
    return colors[category] || colors.default;
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
        {isActive ? 'Activo' : 'Inactivo'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="prompt-manager-loading">
        <div className="spinner-large"></div>
        <p>Cargando prompts...</p>
      </div>
    );
  }

  return (
    <div className="prompt-manager">
      {/* Header */}
      <header className="prompt-manager-header">
        <div className="header-content">
          <div className="header-info">
            <button
              onClick={() => navigate('/admin')}
              className="back-button"
              title="Volver al admin"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <div>
              <h1>Editor de Prompts</h1>
              <p>Gestiona y edita los prompts del agente conversacional</p>
            </div>
          </div>
          
          <button
            onClick={loadPrompts}
            className="refresh-button"
            title="Actualizar prompts"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 2v6h-6M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7M3 22v-6h6M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6-2.3l-3-2.7"/>
            </svg>
          </button>
        </div>
      </header>

      <div className="prompt-manager-content">
        {/* Sidebar - Prompt List */}
        <aside className="prompt-list-sidebar">
          <div className="sidebar-header">
            <h2>Prompts ({prompts.length})</h2>
          </div>
          
          <div className="prompt-list">
            {prompts.map((prompt) => (
              <div
                key={prompt.prompt_key}
                className={`prompt-item ${
                  selectedPrompt?.prompt_key === prompt.prompt_key ? 'selected' : ''
                }`}
                onClick={() => handlePromptSelect(prompt.prompt_key)}
              >
                <div className="prompt-item-header">
                  <div className="prompt-name">{prompt.name}</div>
                  {getStatusBadge(prompt.is_active)}
                </div>
                
                <div className="prompt-item-meta">
                  <span
                    className="category-badge"
                    style={{ backgroundColor: getCategoryColor(prompt.category) }}
                  >
                    {prompt.category}
                  </span>
                  <span className="prompt-version">v{prompt.version}</span>
                </div>
                
                <div className="prompt-description">
                  {prompt.description}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Main Editor */}
        <main className="prompt-editor-main">
          {error && (
            <div className="error-banner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span>{error}</span>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {selectedPrompt ? (
            <PromptEditor
              prompt={selectedPrompt}
              onSave={handlePromptSave}
              saving={saving}
            />
          ) : (
            <div className="no-prompt-selected">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              <h3>Selecciona un prompt</h3>
              <p>Elige un prompt de la lista para comenzar a editarlo</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PromptManagerPage;